import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { AtpService } from 'app/services/atp/atp.service';
import { DocumentService } from 'app/services/document/document.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-atp-example-documents-manager',
  templateUrl: './atp-example-documents-manager.component.html',
  styleUrls: ['./atp-example-documents-manager.component.scss']
})
export class AtpExampleDocumentsManagerComponent implements OnInit {
  public uiData = LEAD_BASED_PAINT;
  public exampleDocumentsByType: { [key: string]: { documentId: number, downloadFileName: string } } = {};
  public uploadingExampleDocumentType: string | null = null;
  public exampleDocumentTypes = [
    { key: 'material', label: 'Material Documents' },
    { key: 'test', label: 'Test Documents' },
    { key: 'assessment', label: 'Assessment Documents' },
    { key: 'qualityControl', label: 'Quality Control Documents' },
    { key: 'certification', label: 'Certification Documents' }
  ];

  constructor(
    private readonly atpService: AtpService,
    private readonly documentService: DocumentService,
    private readonly toastr: ToastrService,
    private readonly dialogRef: MatDialogRef<AtpExampleDocumentsManagerComponent>
  ) {}

  ngOnInit(): void {
    this.loadAtpExampleDocuments();
  }

  close(): void {
    this.dialogRef.close();
  }

  private loadAtpExampleDocuments(): void {
    this.atpService.getATPExampleDocuments().subscribe({
      next: (documents) => {
        this.exampleDocumentsByType = {};
        (documents || []).forEach((doc: any) => {
          if (doc?.documentType && doc?.documentId) {
            this.exampleDocumentsByType[doc.documentType] = {
              documentId: doc.documentId,
              downloadFileName: doc.downloadFileName || doc.fileName
            };
          }
        });
      },
      error: () => {
        this.exampleDocumentsByType = {};
      }
    });
  }

  hasExampleDocument(documentType: string): boolean {
    return !!this.exampleDocumentsByType[documentType];
  }

  triggerExampleDocumentUpload(documentType: string): void {
    const input = document.getElementById(`example-doc-upload-${documentType}`) as HTMLInputElement | null;
    input?.click();
  }

  onExampleDocumentSelected(event: Event, documentType: string): void {
    const target = event.target as HTMLInputElement;
    const file = target?.files?.[0];
    if (!file) {
      return;
    }

    this.uploadingExampleDocumentType = documentType;
    this.atpService.saveATPExampleDocument(file, documentType).subscribe({
      next: () => {
        this.toastr.success('Example document uploaded successfully.');
        this.loadAtpExampleDocuments();
        this.uploadingExampleDocumentType = null;
      },
      error: () => {
        this.toastr.error('Failed to upload example document.');
        this.uploadingExampleDocumentType = null;
      }
    });

    target.value = '';
  }

  downloadExampleDocument(documentType: string): void {
    const doc = this.exampleDocumentsByType[documentType];
    if (!doc?.documentId) {
      this.toastr.warning('No example document is currently configured for this type.', 'No Document');
      return;
    }

    this.documentService.getDocumentContent(doc.documentId).subscribe({
      next: (blob) => this.documentService.saveBlob(blob, doc.downloadFileName),
      error: () => {
        this.toastr.error('Unable to download the example document.', 'Download Error');
      }
    });
  }

  removeExampleDocument(documentType: string): void {
    const doc = this.exampleDocumentsByType[documentType];
    if (!doc?.documentId) {
      return;
    }

    this.atpService.deleteATPExampleDocument(documentType).subscribe({
      next: () => {
        this.toastr.success('Example document removed successfully.');
        this.loadAtpExampleDocuments();
      },
      error: () => {
        this.toastr.error('Failed to remove example document.');
      }
    });
  }
}
